import { Container } from '@/shared/components'
import { Button, H2, BreadcrumbsBar } from '@/common'
import { useCallback, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useSession } from '@/shared/hooks/useSession'
import { useCreateDeclarant } from '@/features/cbam-reporting/hooks/declarants'
import useI18n from '@/shared/hooks/usei18n'
import toast from 'react-hot-toast'
import DeclarantForm from '../../components/DeclarantForm'
import {
  DeclarantFormData,
  getInitialFormData,
  validateDeclarantForm
} from '../../components/DeclarantForm.schema'

export default function DeclarantCreatePage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const {
    session: {
      role,
      organization: { id: organizationId }
    }
  } = useSession()

  const isAdmin = role === 'ADMIN'

  const { mutateAsync: createDeclarant, isPending: isCreating } = useCreateDeclarant(organizationId)

  const [formData, setFormData] = useState<DeclarantFormData>(getInitialFormData())

  const validationResult = useMemo(() => {
    return validateDeclarantForm(formData)
  }, [formData])

  const isSaveDisabled = !validationResult.success

  const onSave = useCallback(async () => {
    if (!validationResult.success) {
      return
    }

    try {
      const newDeclarant = await createDeclarant({
        name: formData.name,
        type: formData.type,
        identificationNumber: formData.identificationNumber,
        country: formData.country,
        street: formData.street || undefined,
        streetNumber: formData.streetNumber || undefined,
        street2: formData.street2 || undefined,
        postCode: formData.postCode || undefined,
        city: formData.city || undefined,
        poBox: formData.poBox || undefined,
        subDivision: formData.subDivision || undefined
      })

      toast.success(t('declarants:create.notifications.success'))
      navigate(`/cbam-reporting/${newDeclarant.id}`)
    } catch (error) {
      console.error('Error creating declarant:', error)
      toast.error(t('declarants:create.notifications.error'))
    }
  }, [validationResult, formData, createDeclarant, navigate, t])

  const onCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const breadcrumbs = [
    { label: t('common:breadcrumbs.cbamReporting'), to: '/cbam-reporting' },
    { label: t('declarants:create.title'), to: '' }
  ]

  if(!isAdmin) {
    return <Navigate to="/cbam-reporting" />
  }

  return (
    <>
      <BreadcrumbsBar links={breadcrumbs} />
      <Container>
        <header>
          <H2>{t('declarants:create.title')}</H2>
        </header>
        <DeclarantForm data={formData} onChange={setFormData} isEditMode={false} />
      </Container>
      <footer className="flex justify-end gap-2 sticky bottom-0 bg-white p-2.5 border-y">
        <Button variant="neutral" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>
        <Button className="w-48" onClick={onSave} isLoading={isCreating} disabled={isSaveDisabled}>
          {t('common:actions.save')}
        </Button>
      </footer>
    </>
  )
}
